import React from 'react';
import createPlotlyComponent from 'react-plotly.js/factory';
import Plotly from 'plotly.js-basic-dist';
import moment from 'moment-timezone';
import { getUnits } from 'src/utils/functs';
import MeasurementAPIs from '../../../../../api/Measurement';
import { splitLineString } from '../../../../../utils/haversine';
import { View } from '../../../../../api/views.types';
import { Spinner } from '../../../../Spinner/Spinner';
import style from './index.module.scss';

const Plot = createPlotlyComponent(Plotly);
const measurementApi = new MeasurementAPIs();

export interface IProfileProps {
  shape: any;
  view: View;
  splitView?: View;
  viewId: string;
  onPointSelected: (point: { lat: number; lng: number }) => void;
}

export interface IProfileState {
  elevation: number[];
  splitViewElevation: number[];
  lengthSample: any;
  loading: boolean;
  error?: any;
}

class ProfileInfo extends React.Component<IProfileProps, IProfileState> {
  constructor(props: IProfileProps) {
    super(props);

    this.state = {
      elevation: [],
      splitViewElevation: [],
      lengthSample: {},
      loading: false,
    };
  }

  public componentDidMount() {
    this.fetchElevation(this.props);
  }

  public UNSAFE_componentWillReceiveProps(newProps: IProfileProps) {
    const {
      shape: newShape,
      viewId: newViewId,
      splitView: newSplitView,
    } = newProps;
    const {
      shape: oldShape,
      viewId: oldViewId,
      splitView: oldSplitView,
    } = this.props;

    if (
      newShape !== oldShape ||
      newViewId !== oldViewId ||
      newSplitView?.id !== oldSplitView?.id
    ) {
      this.fetchElevation(newProps);
    }
  }

  private getPointsFromShape = (shape: any): number[][] => {
    const lineFeature = shape.features[0];
    const { coordinates } = lineFeature.geometry;

    const objs = coordinates.map((lonLat: any) => {
      return { latitude: lonLat[1], longitude: lonLat[0] };
    });

    const { lengths, samples } = splitLineString(objs, 1000);

    this.setState({ lengthSample: { lengths, samples } });

    const resamplingPoints = samples.map((obj) => [
      obj.longitude,
      obj.latitude,
    ]);

    return resamplingPoints;
  };

  private fetchElevation = ({ shape, viewId, splitView }: IProfileProps) => {
    const points = this.getPointsFromShape(shape);

    this.setState({ loading: true }, () => {
      measurementApi
        .fetchElevation(viewId, points)
        .then((response) => {
          const elevation = response.data.elevation.metres;

          this.setState({ elevation, loading: false, error: undefined });
        })
        .catch((res) => {
          console.error('Encountered Error while fetching elevation', res);
          this.setState({
            elevation: [],
            lengthSample: {},
            loading: false,
            error: 'Something went wrong.',
          });
        });
    });

    if (splitView?.id) {
      measurementApi
        .fetchElevation(splitView.id, points)
        .then((response) => {
          const elevation = response.data.elevation.metres;

          this.setState({
            splitViewElevation: elevation,
            loading: false,
            error: undefined,
          });
        })
        .catch((res) => {
          console.error('Encountered Error while fetching elevation', res);
          this.setState({
            splitViewElevation: [],
            error: 'Something went wrong.',
          });
        });
    } else {
      // reset splitViewElevation if no split view
      this.setState({
        splitViewElevation: [],
      });
    }
  };

  private getLatLon = (xvals: number): any => {
    const { lengthSample } = this.state;

    const closest = lengthSample.lengths.reduce(
      (prev: number, curr: number) => {
        return Math.abs(curr - xvals) < Math.abs(prev - xvals) ? curr : prev;
      }
    );

    const index = lengthSample.lengths.indexOf(closest);

    const latLon = lengthSample.samples[index];

    return Object.values(latLon).reverse();
  };

  private convertToCSV = (arr: any[]) => {
    const array = [Object.keys(arr[0])].concat(arr);

    return array
      .map((it) => {
        return Object.values(it).toString();
      })
      .join('\n');
  };

  private getViewElevationFieldName = (view: View): string => {
    const nameArray = [];

    if (view.name) nameArray.push(view.name);

    nameArray.push(moment(view.date.split('T')[0]).format('ll'));
    nameArray.push(`Elevation (${getUnits(view.coordinateUnits)})`);

    return `"${nameArray.join(' - ')}"`;
  };

  private getCSVContent = (): any[] => {
    const { view, splitView } = this.props;
    const { elevation, splitViewElevation, lengthSample } = this.state;
    const { samples } = lengthSample;
    const csvContent: any[] = [];

    if (splitView && splitViewElevation.length > 0) {
      samples.forEach((obj: any, id: number) => {
        csvContent.push({
          longitude: obj.longitude,
          latitude: obj.latitude,
          [this.getViewElevationFieldName(view)]:
            getUnits(view.coordinateUnits) === 'm'
              ? elevation[id]
              : elevation[id] * 3.28084,
          [this.getViewElevationFieldName(splitView)]:
            getUnits(splitView.coordinateUnits) === 'm'
              ? splitViewElevation[id]
              : splitViewElevation[id] * 3.28084,
        });
      });
    } else {
      samples.forEach((obj: any, id: number) => {
        csvContent.push({
          longitude: obj.longitude,
          latitude: obj.latitude,
          [this.getViewElevationFieldName(view)]:
            getUnits(view.coordinateUnits) === 'm'
              ? elevation[id]
              : elevation[id] * 3.28084,
        });
      });
    }

    return csvContent;
  };

  private downloadCSV = () => {
    const csvContent = this.getCSVContent();

    const csvContentDownload = this.convertToCSV(csvContent);

    const pom = document.createElement('a');
    const file = csvContentDownload;
    const blob = new Blob([file], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);

    pom.href = url;
    pom.setAttribute('download', 'samplingPoints.csv');
    pom.click();
  };

  public render(): React.ReactNode {
    const { elevation, lengthSample, loading, error, splitViewElevation } =
      this.state;
    const { onPointSelected, view } = this.props;

    const { coordinateUnits } = view;

    // Axis Units
    const units = getUnits(coordinateUnits);

    // Length sample in feet for x axis
    const lengthArray =
      units !== 'm' && lengthSample.lengths !== undefined
        ? lengthSample.lengths.map((val: number) => {
            return Number(val * 3.28084);
          })
        : lengthSample.lengths;

    // Elevation array for y axis
    const elevationArray =
      units === 'm'
        ? elevation.map((val) => {
            return Number(val.toFixed(2));
          })
        : elevation.map((val) => {
            return Number((val * 3.28084).toFixed(2));
          });

    // Elevation Array for Split View for y axis
    let splitElevationArray;

    if (splitViewElevation) {
      splitElevationArray =
        units === 'm'
          ? splitViewElevation.map((val) => {
              return Number(val.toFixed(2));
            })
          : splitViewElevation.map((val) => {
              return Number((val * 3.28084).toFixed(2));
            });
    }

    if (loading) {
      return <Spinner />;
    }

    if (error) {
      return (
        <div className={style.container}>
          {error.message || 'Something went wrong'}
        </div>
      );
    }

    if (elevation.length === 0) {
      return null;
    }

    return (
      <div>
        <Plot
          data={[
            {
              // x is longitude value
              x: lengthArray,
              // y is latitude value
              y: elevationArray,
              // text value is the elevation
              text: elevationArray,

              type: 'scatter',
              mode: 'lines',
              marker: { color: 'red' },
              textposition: 'top',
              hovertemplate: '%{text}<extra></extra>',
              name: splitElevationArray ? 'Left View' : undefined,
            },
            splitElevationArray
              ? {
                  // x is longitude value
                  x: lengthArray,
                  // y is latitude value
                  y: splitElevationArray,
                  // text value is the elevation
                  text: splitElevationArray,

                  type: 'scatter',
                  mode: 'lines',
                  marker: { color: 'blue' },
                  textposition: 'top',
                  hovertemplate: '%{text}<extra></extra>',
                  name: 'Right View',
                }
              : undefined,
          ]}
          layout={{
            width: 350,
            height: 210,
            // rgb(73, 73, 77)
            plot_bgcolor: 'rgb(73, 73, 77)',
            paper_bgcolor: 'rgb(73, 73, 77)',
            font: { color: 'yellow' },
            margin: {
              l: 50,
              r: 50,
              b: 50,
              t: 50,
              pad: 4,
            },
            xaxis: {
              gridcolor: '#FFFFFF',
              color: 'yellow',
              title: {
                text: `Length (${units})`,
                font: { color: 'yellow' },
              },
            },
            yaxis: {
              gridcolor: '#FFFFFF',
              color: 'yellow',
              title: {
                text: `Elevation (${units})`,
                font: { color: 'yellow' },
              },
            },
          }}
          config={{
            modeBarButtons: [
              [
                {
                  name: 'Download CSV',
                  icon: {
                    width: 48,
                    height: 48,
                    path: 'M35.0042242,11 L35.6235033,11.0013367 C38.845519,11.0246295 40.4501729,11.3555688 42.1147811,12.245811 C43.6798875,13.0828391 44.9171609,14.3201125 45.7642726,15.9044057 L45.7642726,15.9044057 L45.914534,16.1958101 C46.6453722,17.6675669 46.9502065,19.2118503 46.9943027,21.9993856 L46.9943027,21.9993856 L47,22.7555408 L47,37.2556817 L46.9945337,37.974023 C46.9478287,40.9627201 46.6179001,42.4997817 45.754189,44.1147811 C44.9171609,45.6798875 43.6798875,46.9171609 42.0955943,47.7642726 L42.0955943,47.7642726 L41.8041899,47.914534 C40.3324331,48.6453722 38.7881497,48.9502065 36.0006144,48.9943027 L36.0006144,48.9943027 L35.2444592,49 L20.7443183,49 L20.025977,48.9945337 C17.0372799,48.9478287 15.5002183,48.6179001 13.8852189,47.754189 C12.3201125,46.9171609 11.0828391,45.6798875 10.2357274,44.0955943 L10.2357274,44.0955943 L10.085466,43.8041899 C9.31914622,42.2609806 9.02405501,40.6463567 9.00132265,37.6054828 L9.00132265,37.6054828 L9.00132265,22.3764967 C9.0246295,19.154481 9.35556882,17.5498271 10.245811,15.8852189 C11.0828391,14.3201125 12.3201125,13.0828391 13.9044057,12.2357274 L13.9044057,12.2357274 L14.1958101,12.085466 C15.7390194,11.3191462 17.3536433,11.024055 20.3945172,11.0013226 L20.3945172,11.0013226 L20.9997987,11.0012687 C21.8282258,11.001195 22.4998585,11.672708 22.4999323,12.5011351 C22.500006,13.3295623 21.828493,14.0011949 21.0000659,14.0012687 L21.0000659,14.0012687 L20.0730763,14.0049602 C17.6902542,14.0402783 16.5611513,14.2604067 15.5511588,14.7617455 L15.5511588,14.7617455 L15.3000155,14.8912506 C14.2577145,15.4486793 13.4486793,16.2577145 12.8912506,17.3000155 C12.2817685,18.4396479 12.0380753,19.5464775 12.0041905,22.089013 L12.0041905,22.089013 L12,22.7554072 L12.0012806,37.5941158 C12.0208477,40.206854 12.2376186,41.3929399 12.7617455,42.4488412 L12.7617455,42.4488412 L12.8912506,42.6999845 C13.4486793,43.7422855 14.2577145,44.5513207 15.3000155,45.1087494 C16.450087,45.7238144 17.5277176,45.9551266 20.0606374,45.9947589 L20.0606374,45.9947589 L20.7555408,46 L35.2444592,46 C38.0964351,46 39.350611,45.7834009 40.4488412,45.2382545 L40.4488412,45.2382545 L40.6999845,45.1087494 C41.7422855,44.5513207 42.5513207,43.7422855 43.1087494,42.6999845 C43.7238144,41.549913 43.9551266,40.4722824 43.9947589,37.9393626 L43.9947589,37.9393626 L44,37.2444592 L44,22.7555408 C44,19.9035649 43.7834009,18.649389 43.2382545,17.5511588 L43.2382545,17.5511588 L43.1087494,17.3000155 C42.5513207,16.2577145 41.7422855,15.4486793 40.6999845,14.8912506 C39.4653828,14.2309784 38.2692786,14 35.2444592,14 L35.2444592,14 L35.0042242,14 C34.1757971,14 33.5042242,13.3284271 33.5042242,12.5 C33.5042242,11.6715729 34.1757971,11 35.0042242,11 L35.0042242,11 Z M28,5 C28.8284271,5 29.5,5.67157288 29.5,6.5 L29.5,6.5 L29.4993226,33.878 L34.4393398,28.9393398 C34.9885146,28.390165 35.857577,28.3558416 36.4467378,28.8363696 L36.5606602,28.9393398 C37.1464466,29.5251263 37.1464466,30.4748737 36.5606602,31.0606602 L36.5606602,31.0606602 L29.0606602,38.5606602 C28.4748737,39.1464466 27.5251263,39.1464466 26.9393398,38.5606602 L26.9393398,38.5606602 L19.4393398,31.0606602 C18.8535534,30.4748737 18.8535534,29.5251263 19.4393398,28.9393398 C20.0251263,28.3535534 20.9748737,28.3535534 21.5606602,28.9393398 L21.5606602,28.9393398 L26.4993226,33.878 L26.5,6.5 C26.5,5.72030388 27.0948881,5.07955132 27.85554,5.00686658 Z',
                  },
                  click: () => this.downloadCSV(),
                },
                'toImage',
                'pan2d',
                'zoomIn2d',
                'zoomOut2d',
                'resetScale2d',
              ],
            ],
            displaylogo: false,
          }}
          onHover={(e: any) => {
            const latLon = this.getLatLon(
              units !== 'm' ? e.xvals[0] / 3.28084 : e.xvals[0]
            );

            onPointSelected({ lat: latLon[1], lng: latLon[0] });
          }}
        />
      </div>
    );
  }
}

export default ProfileInfo;
